import { computed, effect, signal, type Signal } from '@preact/signals-react'
import type { MoveHistory } from '../../game/moveHistory/moveHistory.types'
import type { LocalMates } from '../../localMates/localMates/localMates.types'
import type { Mode } from '../../mode/mode/mode.types'
import type { FocusNavigation } from '../../mode/focusNavigation/focusNavigation.types'
import type { ControllerInput } from '@merels/input/controllerInput'
import type { GameRegistry } from '../../catalog/gameRegistry/gameRegistry.types'
import type { DebugPanelVM } from '../debugPanelVM/debugPanelVM.types'
import type { RoomDep } from '../../room/room.types'
import { getEffectiveGameOptionsForStart } from '../../room/roomActions/modules/gameOptions.actions'
import type {
  GamepadEmulation,
  EmulatedControllerId,
  EmulationSelectionId,
} from '../../debug/gamepadEmulation/gamepadEmulation.types'
import type {
  ShellControllerIndicatorBadgeBgColor,
  ShellControllerIndicatorIconOpacity,
  ShellVMDep,
} from './shellVM.types'

function isEmulatedControllerId(id: EmulationSelectionId | null): id is EmulatedControllerId {
  return id === 'X1' || id === 'X2' || id === 'X3'
}

import type { AuthDep } from '../../auth/auth.types'

export type ShellVMDeps = {
  roomState: RoomDep['state']
  roomConnection: RoomDep['connection']
  roomActions: RoomDep['actions']
  gameRegistry: GameRegistry
  localMates: LocalMates
  roomName: Signal<string | null>
  debugPanelVM: DebugPanelVM
  moveHistory: MoveHistory
  mode: Mode
  focusNavigation: FocusNavigation
  controllerInput: ControllerInput
  gamepadEmulation: GamepadEmulation
  auth: AuthDep
}

export function makeShellVMDep(deps: ShellVMDeps): ShellVMDep {
  const {
    roomState,
    roomConnection,
    roomActions,
    gameRegistry,
    localMates,
    roomName,
    debugPanelVM,
    moveHistory,
    mode,
    focusNavigation,
    gamepadEmulation,
    auth,
  } = deps

  const configureDialogOpen = signal<boolean>(false)
  const gameInfoDialogOpen = signal<boolean>(false)
  const closeConfirmDialogOpen = signal<boolean>(false)
  const shareDialogOpen = signal<boolean>(false)
  const gameHintsEnabled = signal<boolean>(false)
  const gameViewMode = signal<'play' | 'rulebook'>('play')
  const previousRulebookGameIdRef = { current: roomState.activeGame.value?.gameId ?? null }
  const previousHintsGameIdRef = { current: roomState.activeGame.value?.gameId ?? null }

  // Reset close-confirm dialog when game ends or when a new game starts.
  // This prevents the dialog from erroneously showing when opening the next game
  // (e.g. after closing without confirmation left stale state).
  const prevActiveGameRef = { current: roomState.activeGame.value }
  effect(() => {
    const activeGame = roomState.activeGame.value
    if (!activeGame) {
      closeConfirmDialogOpen.value = false
    } else if (!prevActiveGameRef.current) {
      // Transition from no game to active game: new game started, ensure dialog is closed
      closeConfirmDialogOpen.value = false
    }
    prevActiveGameRef.current = activeGame
  })

  const isVisible = computed(() => gamepadEmulation.isEnabled.value)

  const title = computed(() => {
    const selectedControllerId = gamepadEmulation.selectedControllerId.value
    if (isEmulatedControllerId(selectedControllerId)) {
      const isConnected = Boolean(
        gamepadEmulation.controllers.value.find((c) => c.id === selectedControllerId)?.connected,
      )
      return `Selected controller: ${selectedControllerId}${isConnected ? '' : ' (disconnected)'}`
    }
    if (selectedControllerId === 'K') return 'Keyboard selected'
    return 'No controller selected'
  })

  const badgeBgColor = computed<ShellControllerIndicatorBadgeBgColor>(() => {
    const selectedControllerId = gamepadEmulation.selectedControllerId.value
    return selectedControllerId === 'X1'
      ? 'red'
      : selectedControllerId === 'X2'
        ? 'blue'
        : selectedControllerId === 'X3'
          ? 'lime'
          : 'gray'
  })

  const iconOpacity = computed<ShellControllerIndicatorIconOpacity>(() => {
    const selectedControllerId = gamepadEmulation.selectedControllerId.value
    if (!isEmulatedControllerId(selectedControllerId)) return 'high'

    const isConnected = Boolean(
      gamepadEmulation.controllers.value.find((c) => c.id === selectedControllerId)?.connected,
    )
    return isConnected ? 'high' : 'low'
  })

  const gameId = computed(() => roomState.activeGame.value?.gameId ?? null)
  const isGameOver = computed(() => Boolean(roomState.activeGame.value?.ctx?.gameover))
  const hasMovesBeenMade = computed(() => moveHistory.entries.value.length > 0)

  const gameMetadata = computed(() => {
    const id = gameId.value
    return id ? gameRegistry.getGameMetadata?.(id) : null
  })

  const gameDescription = computed(() => gameMetadata.value?.description)
  const gameName = computed(() => gameMetadata.value?.displayName || gameId.value)
  // Every game has the platform's Host plays setting in addition to its own options.
  const hasGameOptions = computed(() => Boolean(gameMetadata.value))
  const hasHints = computed(() => {
    const id = gameId.value
    return id ? gameRegistry.supportsHints(id) : false
  })
  const hasRulebook = computed(() => {
    const id = gameId.value
    return id ? gameRegistry.supportsRulebook(id) : false
  })

  const hasTimers = computed(() => {
    const activeGame = roomState.activeGame.value
    const id = activeGame?.gameId
    if (!id || !activeGame) return false
    const playerCount = activeGame?.playerToMateIds?.length || 2
    const options = getEffectiveGameOptionsForStart(id, playerCount, roomState.savedOptions.value, gameRegistry)
    return Boolean(options?.timedMoves && options.timedMoves !== 'off')
  })

  const mateName = computed(() => localMates.localMates.value[0]?.name || '')
  const roomNameString = computed(() => roomName.value || roomConnection.roomId.value || '')
  const ownerNameString = computed(() => roomState.ownerName.value || 'Someone')
  // window.location.href is only meaningful once we've actually joined a room; roomId
  // becoming truthy is our signal that the URL has settled (mirrors copyRoomLink's guard).
  const roomUrl = computed(() => {
    if (typeof window === 'undefined' || !roomConnection.roomId.value) return ''
    return window.location.href
  })

  const isOwner = computed(() => {
    const user = auth.currentUser.value
    if (!user) return false
    return roomState.ownerId.value === user.id
  })

  const focusIsActive = focusNavigation.isActive
  const isHeaderFocused = computed(
    () => focusNavigation.isActive.value && focusNavigation.focusArea.value === 'header',
  )

  effect(() => {
    const activeGameId = gameId.value
    const supported = activeGameId ? gameRegistry.supportsRulebook(activeGameId) : false
    const gameChanged = previousRulebookGameIdRef.current !== activeGameId
    const ended = isGameOver.value

    if (gameChanged || ended || !supported) {
      gameViewMode.value = 'play'
    }

    previousRulebookGameIdRef.current = activeGameId
  })

  effect(() => {
    const activeGameId = gameId.value
    const supported = activeGameId ? gameRegistry.supportsHints(activeGameId) : false
    const gameChanged = previousHintsGameIdRef.current !== activeGameId

    if (gameChanged || !supported) {
      gameHintsEnabled.value = false
    }

    previousHintsGameIdRef.current = activeGameId
  })

  // Auto-pause/resume: move this "mode orchestration" out of React effects.
  effect(() => {
    if (!hasTimers.value) return
    if (isGameOver.value) return
    if (!focusNavigation.isActive.value) return

    const focusArea = focusNavigation.focusArea.value
    if (focusArea === 'header') {
      mode.isGamePaused.value = true
      return
    }
    if (focusArea === 'content') {
      mode.isGamePaused.value = false
    }
  })

  function toggleDebugSidebar(): void {
    debugPanelVM.setIsOpen(!debugPanelVM.isOpen.value)
  }

  async function copyRoomLink(): Promise<boolean> {
    if (typeof window === 'undefined') return false
    if (!roomConnection.roomId.value) return false
    try {
      await navigator.clipboard.writeText(window.location.href)
      return true
    } catch {
      return false
    }
  }

  async function leaveRoom(): Promise<void> {
    await roomConnection.leaveRoom()
  }

  async function closeGame(): Promise<void> {
    closeConfirmDialogOpen.value = false
    // If we're in game setup phase, cancel the setup.
    if (roomState.gameSetup.value) {
      await roomActions.cancelGameSetup()
      return
    }
    // If we have an active game, end it.
    if (roomState.activeGame.value) {
      await roomActions.endGame()
    }
  }

  async function playAgain(): Promise<void> {
    if (!roomState.canControlGames.value) return
    await roomActions.restartGame()
  }

  async function setManagedMode(managed: boolean): Promise<void> {
    await roomActions.setManagedMode(managed)
  }

  async function setHostTerm(hostTerm: string): Promise<void> {
    await roomActions.setHostTerm(hostTerm)
  }

  async function setCuratedMode(curated: boolean): Promise<void> {
    await roomActions.setCuratedMode(curated)
  }

  function togglePause(): void {
    mode.isGamePaused.value = !mode.isGamePaused.value
  }

  function toggleGameless(): void {
    void roomActions.setGamelessMode(!roomState.gameless.value)
  }

  function openMeDialog(): void {
    localMates.showNameDialog.value = true
  }

  function openConfigure(): void {
    if (!roomState.canControlGames.value) return
    configureDialogOpen.value = true
  }

  function toggleHints(): void {
    if (!hasHints.value) {
      return
    }
    gameHintsEnabled.value = !gameHintsEnabled.value
  }

  function toggleRulebook(): void {
    if (!hasRulebook.value) {
      return
    }
    gameViewMode.value = gameViewMode.value === 'play' ? 'rulebook' : 'play'
  }

  function requestCloseGame(): void {
    if (!roomState.canControlGames.value) return
    // Mirrors the old GameControls behavior:
    // - If game is over: close immediately
    // - If moves were made: ask for confirmation
    // - Otherwise: close immediately
    if (isGameOver.value) {
      void closeGame()
      return
    }
    if (hasMovesBeenMade.value) {
      closeConfirmDialogOpen.value = true
      return
    }
    void closeGame()
  }

  async function confirmCloseGame(): Promise<void> {
    closeConfirmDialogOpen.value = false
    await closeGame()
  }

  return {
    controllerIndicator: { isVisible, title, badgeBgColor, iconOpacity },
    game: {
      gameId,
      gameName,
      gameDescription,
      isGameOver,
      hasMovesBeenMade,
      hasGameOptions,
      hasHints,
      hasRulebook,
      hasTimers,
      isGamePaused: mode.isGamePaused,
      hintsEnabled: gameHintsEnabled,
      viewMode: gameViewMode,
    },
    gameControls: {
      infoDialog: {
        isOpen: gameInfoDialogOpen,
        setIsOpen: (next) => {
          gameInfoDialogOpen.value = next
        },
      },
      closeConfirmDialog: {
        isOpen: closeConfirmDialogOpen,
        setIsOpen: (next) => {
          closeConfirmDialogOpen.value = next
        },
      },
      requestCloseGame,
      confirmCloseGame,
      openConfigure,
      toggleHints,
      toggleRulebook,
    },
    room: {
      mateName,
      roomName: roomNameString,
      ownerName: ownerNameString,
      ownerId: roomState.ownerId,
      imageUrl: roomState.imageUrl,
      videoEnabled: roomState.videoEnabled,
      isClosed: roomState.isClosed,
      opensAt: roomState.opensAt,
      gameless: roomState.gameless,
      isOwner,
      roomUrl,
      managed: roomState.managed,
      hostTerm: roomState.hostTerm,
      curated: roomState.curated,
      canControlGames: roomState.canControlGames,
    },
    focus: {
      isActive: focusIsActive,
      isHeaderFocused,
    },
    debugSidebarOpen: debugPanelVM.isOpen,
    isOwnedGame: debugPanelVM.isOwnedGame,
    configureDialog: {
      isOpen: configureDialogOpen,
      setIsOpen: (next) => {
        configureDialogOpen.value = next
      },
    },
    shareDialog: {
      isOpen: shareDialogOpen,
      setIsOpen: (next) => {
        shareDialogOpen.value = next
      },
    },
    actions: {
      toggleDebugSidebar,
      copyRoomLink,
      leaveRoom,
      updateRoom: roomConnection.updateRoom,
      closeGame,
      playAgain,
      togglePause,
      toggleGameless,
      openMeDialog,
      setManagedMode,
      setHostTerm,
      setCuratedMode,
    },
    auth,
  }
}
